import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Button, 
  Space, 
  Typography, 
  Card,
  message,
  Radio
} from 'antd';
import { 
  YoutubeOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { authAxios } from '../utils/session_utils';
import { YouTubeLiveStream, Video } from './types';
import ChannelSelector from './ChannelSelector';
import ManualRTMPConfig from './ManualRTMPConfig';
import OAuthYouTubeSchedule from './OAuthYouTubeSchedule';

const { Text } = Typography;

interface StreamSchedulerProps {
  stream: YouTubeLiveStream;
  videos: Video[];
  onSchedule: () => void;
  onCancel: () => void;
}

const StreamScheduler: React.FC<StreamSchedulerProps> = ({
  stream,
  videos,
  onSchedule,
  onCancel
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [, setCheckingConnection] = useState(false);
  const [channelInfo, setChannelInfo] = useState<{ id: string; title: string; country?: string } | null>(null);
  const [selectedTokenId, setSelectedTokenId] = useState<string | undefined>();
  const [showChannelSelector, setShowChannelSelector] = useState(false);
  const [streamMode, setStreamMode] = useState<'oauth' | 'manual'>(stream.stream_mode || 'manual');
  const [tokenRefreshTrigger, setTokenRefreshTrigger] = useState(0);

  // Handle mode change - unlink YouTube connection when switching to manual mode
  const handleModeChange = async (newMode: 'oauth' | 'manual') => {
    // Only unlink if switching from OAuth to manual
    if (newMode === 'manual' && streamMode === 'oauth') {
      try {
        // Unlink the YouTube OAuth token
        await authAxios({
          method: 'DELETE',
          url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${stream.id}/dissociate-token`
        });

        // Clear YouTube-specific fields
        await authAxios({
          method: 'PUT',
          url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${stream.id}`,
          data: {
            video_url: null,
            caption_ingestion_url: null
          }
        });

        // Reset connection state
        setIsConnected(false);
        setSelectedTokenId(undefined);
        setChannelInfo(null);
        setShowChannelSelector(false);

        message.success('YouTube connection unlinked and YouTube-specific data cleared');
      } catch (error: unknown) {
        console.error('Error unlinking YouTube connection:', error);
        const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to unlink YouTube connection';
        message.error(errorMessage);
        // Don't change mode if unlinking failed
        return;
      }
    }
    
    setStreamMode(newMode);
  };

  useEffect(() => {
    setStreamMode(stream.stream_mode || 'manual');
    setIsConnected(false);
    setSelectedTokenId(undefined);
    setChannelInfo(null);
    setShowChannelSelector(false);
    checkYouTubeConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream.id]);

  const fetchChannelInfo = async (tokenId: string) => {
    const response = await authAxios({
      method: 'GET',
      url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/channel-id`,
      params: { token_id: tokenId }
    });
    
    if (response.data?.channelId) {
      setChannelInfo(response.data.channelId);
    }
  };

  const checkYouTubeConnection = async () => {
    setCheckingConnection(true);
    try {
      // Check if stream has an associated OAuth token
      const response = await authAxios({
        method: 'GET',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${stream.id}/token`
      });
      
      if (response.data?.token) {
        // Stream has an associated token - verify it's still valid by trying to get channel info
        try {
          await fetchChannelInfo(response.data.token.id);
          // Token is valid
          setIsConnected(true);
          setSelectedTokenId(response.data.token.id);
          setChannelInfo({
            id: response.data.token.youtube_channel_id,
            title: response.data.token.youtube_channel_title
          });
        } catch (channelError: any) {
          // Token might be expired - check if it's an OAuth error
          const isTokenExpired = channelError.response?.data?.error_code === 'OAUTH_TOKEN_EXPIRED' ||
                                channelError.response?.status === 401 ||
                                channelError.message?.includes('invalid_grant');
          
          if (isTokenExpired) {
            // Token is expired, show channel selector to allow reconnection
            message.warning('Your YouTube account connection has expired. Please reconnect your account.');
            setIsConnected(false);
            setShowChannelSelector(true);
            setSelectedTokenId(undefined);
            setChannelInfo(null);
            setTokenRefreshTrigger(t => t + 1);
          } else {
            // Other error, but token exists - still show as connected
            setIsConnected(true);
            setSelectedTokenId(response.data.token.id);
            setChannelInfo({
              id: response.data.token.youtube_channel_id,
              title: response.data.token.youtube_channel_title
            });
          }
        }
      } else {
        // No token associated yet
        setIsConnected(false);
        setShowChannelSelector(true);
      }
    } catch (error: any) {
      // Check if error indicates token expiration
      const isTokenExpired = error.response?.data?.error_code === 'OAUTH_TOKEN_EXPIRED' ||
                            error.response?.status === 401;

      if (isTokenExpired) {
        message.warning('Your YouTube account connection has expired. Please reconnect your account.');
        setTokenRefreshTrigger(t => t + 1);
      }

      // No token associated yet or token expired
      setIsConnected(false);
      setShowChannelSelector(true);
      setChannelInfo(null);
    } finally {
      setCheckingConnection(false);
    }
  };

  const handleTokenSelected = (tokenId: string, channelInfo: { id: string; title: string }) => {
    setSelectedTokenId(tokenId);
    setChannelInfo(channelInfo);
    setIsConnected(true);
    setShowChannelSelector(false);
    message.success('YouTube channel selected successfully');
  };

  const handleConnectNewChannel = () => {
    setShowChannelSelector(true);
  };

  const handleChangeChannel = () => {
    setShowChannelSelector(true);
  };

  const handleDeleteChannel = async () => {
    if (!selectedTokenId) {
      message.error('No channel selected to delete');
      return;
    }

    try {
      // First, dissociate the token from the stream if it's associated
      try {
        await authAxios({
          method: 'DELETE',
          url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${stream.id}/dissociate-token`
        });
      } catch (dissociateError) {
        // Ignore errors if token is not associated with stream
        console.log('Token may not be associated with stream:', dissociateError);
      }

      // Delete the OAuth token
      await authAxios({
        method: 'DELETE',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/oauth-tokens/${selectedTokenId}`
      });

      // Reset connection state
      setIsConnected(false);
      setSelectedTokenId(undefined);
      setChannelInfo(null);
      setShowChannelSelector(true);

      message.success('YouTube channel deleted successfully');
    } catch (error: unknown) {
      console.error('Error deleting YouTube channel:', error);
      const errorResponse = error as { 
        response?: { 
          status?: number;
          statusText?: string;
          data?: { error?: string; message?: string } 
        }; 
        message?: string 
      };
      const errorMessage = errorResponse?.response?.data?.error || 
                          errorResponse?.response?.data?.message || 
                          errorResponse?.message || 
                          'Failed to delete YouTube channel';
      console.error('Full error details:', {
        status: errorResponse?.response?.status,
        statusText: errorResponse?.response?.statusText,
        data: errorResponse?.response?.data,
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/oauth-tokens/${selectedTokenId}`
      });
      message.error(errorMessage);
    }
  };

  const handleSchedule = async () => {
    try {
      if (!isConnected || !selectedTokenId) {
        message.error('Please select a YouTube channel first');
        return;
      }

      // Validate Twitch form fields
      try {
        await form.validateFields();
      } catch (validationError: any) {
        if (validationError.errorFields) {
          for (const field of validationError.errorFields) {
            message.error(field.errors[0]);
          }
        }
        return;
      }

      setLoading(true);

      const twitchValues = form.getFieldsValue(['twitch_rtmp_url', 'twitch_stream_key']);
      const requestData: any = {
        stream_mode: 'oauth',
        video_selection_mode: 'automatic'
      };

      // Add Twitch fields if provided
      if (twitchValues.twitch_rtmp_url && twitchValues.twitch_stream_key) {
        requestData.twitch_rtmp_url = twitchValues.twitch_rtmp_url;
        requestData.twitch_stream_key = twitchValues.twitch_stream_key;
      }

      // Save configuration to database only (no YouTube API calls)
      await authAxios({
        method: 'PUT',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${stream.id}`,
        data: requestData
      });
      
      message.success('Configuration saved successfully');
      onSchedule();
    } catch (error: any) {
      console.error('Error saving configuration:', error);
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('Failed to save configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManualSchedule = async () => {
    try {
      // Validate form fields - this will throw if validation fails
      // Ant Design will automatically display field-level errors
      let values;
      try {
        values = await form.validateFields();
      } catch (validationError: any) {
        if (validationError.errorFields) {
          for(const field of validationError.errorFields) {
            message.error(field.errors[0]);
          }
        }
        return;
      }
      
      if (videos.length === 0 && stream.video_selection_mode !== 'automatic') {
        message.error('Please add videos to the stream first');
        return;
      }
      
      setLoading(true);
      
      // Save configuration to database only (no YouTube API calls)
      const requestData: any = {
        rtmp_url: values.rtmp_url,
        stream_key: values.stream_key,
        caption_ingestion_url: values.caption_ingestion_url || null,
        stream_mode: 'manual'
      };
      
      // Add Twitch fields if provided
      if (values.twitch_rtmp_url && values.twitch_stream_key) {
        requestData.twitch_rtmp_url = values.twitch_rtmp_url;
        requestData.twitch_stream_key = values.twitch_stream_key;
      }
      
      await authAxios({
        method: 'PUT',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${stream.id}`,
        data: requestData
      });
      
      message.success('Configuration saved successfully');
      onSchedule();
    } catch (error: any) {
      console.error('Error saving configuration:', error);
      // Only show error message if it's not a form validation error
      // (validation errors are already displayed by Ant Design)
      if (!error?.errorFields) {
        if (error.response?.data?.error) {
          message.error(error.response.data.error);
        } else {
          message.error('Failed to save configuration');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      await authAxios({
        method: 'POST',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${stream.id}/cancel`
      });
      message.success('Stream cancelled successfully');
      onCancel();
    } catch (error) {
      console.error('Error cancelling stream:', error);
      message.error('Failed to cancel stream');
    }
  };

  // Amagi HLS-pull streams have no RTMP/oauth output config at all: the
  // playout server serves /hls/main.m3u8 and Amagi pulls it. Everything to
  // configure is (a) assign a playout server, (b) import a schedule CSV.
  if (stream.output_target === 'amagi_hls') {
    const endpoint = stream.playout_server?.endpoint_url?.replace(/\/+$/, '');
    return (
      <Card size="small">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text strong>Amagi (HLS pull) — no RTMP configuration needed</Text>
          <Text type="secondary">
            This stream serves an HLS feed from its playout server; Amagi pulls it
            directly. Configure it by assigning a playout server (from the stream
            list) and importing a schedule CSV in the Schedule tab.
          </Text>
          {endpoint ? (
            <Text>
              Amagi pull URL: <Text code copyable>{`${endpoint}/hls/main.m3u8`}</Text>
            </Text>
          ) : (
            <Text type="warning">
              No playout server assigned yet — assign one to get the Amagi pull URL.
            </Text>
          )}
          {stream.timezone && (
            <Text type="secondary">Broadcast timezone: {stream.timezone}</Text>
          )}
        </Space>
      </Card>
    );
  }

  return (
    <div>
      {/* Mode Selection Toggle */}
      <Card size="small" style={{ marginBottom: '24px' }}>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text strong>Configuration Mode:</Text>
          <Radio.Group 
            value={streamMode} 
            onChange={(e) => handleModeChange(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="oauth">
              <Space>
                <YoutubeOutlined />
                OAuth (YouTube API)
              </Space>
            </Radio.Button>
            <Radio.Button value="manual">
              <Space>
                <SettingOutlined />
                Manual RTMP
              </Space>
            </Radio.Button>
          </Radio.Group>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {streamMode === 'oauth' 
              ? 'Use YouTube OAuth to automatically create and schedule the stream'
              : 'Manually configure RTMP URL and stream key (for non-OAuth scenarios)'}
          </Text>
        </Space>
      </Card>

      {streamMode === 'oauth' && (showChannelSelector || !isConnected) ? (
        <ChannelSelector
          streamId={stream.id}
          userId={stream.created_by}
          selectedTokenId={selectedTokenId}
          onTokenSelected={handleTokenSelected}
          onConnectNew={handleConnectNewChannel}
          refreshTrigger={tokenRefreshTrigger}
        />
      ) : streamMode === 'oauth' ? (
        <OAuthYouTubeSchedule
          channelInfo={channelInfo}
          selectedTokenId={selectedTokenId}
          stream={stream}
          form={form}
          onCancel={onCancel}
          onSchedule={handleSchedule}
          onCancelStream={handleCancel}
          onChangeChannel={handleChangeChannel}
          onDeleteChannel={handleDeleteChannel}
          isLive={stream.status === 'live'}
        />
      ) : (
        <ManualRTMPConfig
          stream={stream}
          form={form}
          loading={loading}
          onCancel={onCancel}
          onConfigure={handleManualSchedule}
          isLive={stream.status === 'live'}
        />
      )}
    </div>
  );
};

export default StreamScheduler;
